import {
  AssociationRecord,
  AssociationType,
} from "@/shared/domain/association";
import { EventRecord } from "@/shared/domain/events";
import { backingFileId } from "@/shared/domain/events/resolveEventScope";
import { TopicRecord } from "@/shared/domain/topics";
import { LoggerService } from "@/shared/services";
import { PlotSeries } from "@/shared/state/visualization";
import { XAxisAlignment } from "@/shared/state/visualization/schema/v1";

import {
  groupSourcesByFileId,
  WorkspaceSource,
} from "../../WorkspaceCtx/workspaceSources";

import {
  AlignedSeries,
  isEventAnchored,
  parseNanosecondsString,
  SeriesAlignment,
} from "./alignment";

interface ResolveSeriesAlignmentParams {
  series: PlotSeries[];
  sources: WorkspaceSource[];
  alignment: XAxisAlignment;
  /** Workspace events; only consulted for a session alignment's event anchor. */
  events: EventRecord[];
  /**
   * Workspace topics; used to map topic-associated events to their file, and
   * to find each source's earliest topic start under the first-data anchor.
   */
  topics: TopicRecord[];
}

/**
 * The file backing a topic an event association points at, or undefined when
 * the workspace has no such topic. A topic associated with a dataset rather
 * than a file backs no file: `backingFileId` throws, and the throw is logged
 * and the association dropped so the event's other associations still
 * resolve, as `resolveEventScope` does.
 */
function tryBackingFileId(topic: TopicRecord | undefined): string | undefined {
  if (topic === undefined) {
    return undefined;
  }
  try {
    return backingFileId(topic);
  } catch (err) {
    LoggerService.error(
      `Skipping unresolvable event association ${topic.topic_id}`,
      err,
    );
    return undefined;
  }
}

/**
 * The file id an event association resolves to, or undefined when it names no
 * single file:
 * - file: the association's own id.
 * - topic: the file backing that topic.
 * - message path: the file backing the path's topic; this is the kind an
 *   event scoped to a plot panel's series carries.
 * - dataset: undefined, since a dataset covers more than one recording
 *   session.
 */
function associationFileId(
  association: AssociationRecord,
  topicById: Map<string, TopicRecord>,
  topicByMessagePathId: Map<string, TopicRecord>,
): string | undefined {
  switch (association.association_type) {
    case AssociationType.File:
      return association.association_id;
    case AssociationType.Topic:
      return tryBackingFileId(topicById.get(association.association_id));
    case AssociationType.MessagePath:
      return tryBackingFileId(
        topicByMessagePathId.get(association.association_id),
      );
    case AssociationType.Dataset:
      return undefined;
  }
}

/**
 * The start of the earliest event named `eventName` in each source, keyed by
 * source id. An event belongs to a source when one of its associations
 * resolves to a file that source holds (see {@link associationFileId}); an
 * event whose associations resolve to no file anchors nothing. An event on a
 * file two sessions both hold anchors each of them.
 */
function earliestEventStartBySource(
  eventName: string,
  events: EventRecord[],
  sourcesByFileId: Map<string, WorkspaceSource[]>,
  topics: TopicRecord[],
): Map<string, bigint> {
  const topicById = new Map(topics.map((topic) => [topic.topic_id, topic]));
  const topicByMessagePathId = new Map(
    topics.flatMap((topic) =>
      topic.message_paths.map((messagePath): [string, TopicRecord] => [
        messagePath.message_path_id,
        topic,
      ]),
    ),
  );

  const earliestBySource = new Map<string, bigint>();
  for (const event of events) {
    if (event.name !== eventName) {
      continue;
    }
    for (const association of event.associations) {
      const fileId = associationFileId(
        association,
        topicById,
        topicByMessagePathId,
      );
      if (fileId === undefined) {
        continue;
      }
      for (const source of sourcesByFileId.get(fileId) ?? []) {
        const earliest = earliestBySource.get(source.sourceId);
        if (earliest === undefined || event.start_time < earliest) {
          earliestBySource.set(source.sourceId, event.start_time);
        }
      }
    }
  }
  return earliestBySource;
}

/** Whether a timestamp lies inside a recording's span, endpoints included. */
function isWithinSpan(
  time: bigint,
  span: { startTime: bigint; endTime: bigint },
): boolean {
  return time >= span.startTime && time <= span.endTime;
}

/**
 * The earliest topic start in each source, keyed by source id. Every topic
 * of the source counts, plotted or not; the source is the alignment unit, so
 * its zero comes from all of its data. A topic with no recorded start anchors
 * nothing, a start outside a source's span cannot anchor that source, and a
 * topic associated with a dataset rather than a file belongs to no source.
 *
 * A topic on a file two sessions both hold counts toward each of them, tested
 * against each session's own span: a session that clips the file past the
 * topic's start skips it, while a session taking the file whole anchors on it.
 */
function earliestTopicStartBySource(
  sourcesByFileId: Map<string, WorkspaceSource[]>,
  topics: TopicRecord[],
): Map<string, bigint> {
  const earliestBySource = new Map<string, bigint>();
  for (const topic of topics) {
    if (topic.association.association_type !== AssociationType.File) {
      continue;
    }
    const start = topic.start_time;
    if (start === null || start === undefined) {
      continue;
    }
    const fileId = topic.association.association_id;
    for (const source of sourcesByFileId.get(fileId) ?? []) {
      if (!isWithinSpan(start, source)) {
        continue;
      }
      const earliest = earliestBySource.get(source.sourceId);
      if (earliest === undefined || start < earliest) {
        earliestBySource.set(source.sourceId, start);
      }
    }
  }
  return earliestBySource;
}

interface SeriesRecording {
  sourceId: string;
  startTime: bigint;
  endTime: bigint;
  /**
   * True when the span comes from the series' own persisted topic bounds
   * rather than a workspace source.
   */
  fromTopicBounds: boolean;
}

/**
 * The recording a series belongs to: its file's workspace source when one
 * exists, else a span synthesized from the series' own persisted topic
 * bounds. The synthesized span keeps a series whose file matches no source
 * (a topic associated with a dataset rather than a file, or a file with no
 * live topic time bounds) aligned to its own data; a series with no entry in
 * the result plots against the panel-wide basis instead (see
 * {@link SeriesAlignment}). Undefined when neither is available; such a
 * series cannot be aligned at all.
 *
 * A series carries one basis and one span, so a file two sessions both hold
 * resolves against one of them: the first source listed for the file, which is
 * the earliest-starting session, since `deriveWorkspaceSources` orders sources
 * by span start and `groupSourcesByFileId` keeps that order. The other session
 * still counts the file when it anchors itself, and still covers it in its own
 * span.
 */
function seriesRecording(
  seriesItem: PlotSeries,
  sourcesByFileId: Map<string, WorkspaceSource[]>,
): SeriesRecording | undefined {
  const association = seriesItem.data.topic.association;
  const source =
    association.association_type === AssociationType.File
      ? sourcesByFileId.get(association.association_id)?.[0]
      : undefined;
  if (source !== undefined) {
    return {
      sourceId: source.sourceId,
      startTime: source.startTime,
      endTime: source.endTime,
      fromTopicBounds: false,
    };
  }

  const topicStart = parseNanosecondsString(seriesItem.data.topic.startTime);
  const topicEnd = parseNanosecondsString(seriesItem.data.topic.endTime);
  if (topicStart === undefined || topicEnd === undefined) {
    return undefined;
  }
  return {
    sourceId: `topic:${seriesItem.data.topic.id}`,
    startTime: topicStart,
    endTime: topicEnd,
    fromTopicBounds: true,
  };
}

/**
 * The basis a series plots as x = 0 under the panel's alignment, and whether
 * that basis is a fallback (the requested alignment could not be resolved,
 * so the recording's start substitutes). Exhaustive over both the unit and
 * the session-anchor unions: adding a unit or anchor kind fails compilation
 * here.
 */
function resolveBasis(
  alignment: XAxisAlignment,
  recording: SeriesRecording,
  topicStart: bigint | undefined,
  eventStartBySource: Map<string, bigint> | undefined,
  topicStartBySource: Map<string, bigint> | undefined,
): { basis: bigint; isFallback: boolean } {
  switch (alignment.unit) {
    case "series":
      // A persisted topic start outside the recording's span (e.g. before a
      // session's clip range) cannot anchor the series within the recording,
      // so it is treated the same as an absent start.
      if (topicStart === undefined || !isWithinSpan(topicStart, recording)) {
        return { basis: recording.startTime, isFallback: true };
      }
      return { basis: topicStart, isFallback: false };
    case "session": {
      const anchor = alignment.anchor;
      if (typeof anchor === "object") {
        const eventStart = eventStartBySource?.get(recording.sourceId);
        if (eventStart === undefined) {
          return { basis: recording.startTime, isFallback: true };
        }
        return { basis: eventStart, isFallback: false };
      }
      switch (anchor) {
        case "session-start":
          // A recording synthesized from the series' own topic bounds has no
          // known session start, so anchoring to it is a flagged fallback.
          return {
            basis: recording.startTime,
            isFallback: recording.fromTopicBounds,
          };
        case "first-data": {
          const earliestTopicStart = topicStartBySource?.get(
            recording.sourceId,
          );
          if (earliestTopicStart === undefined) {
            return { basis: recording.startTime, isFallback: true };
          }
          return { basis: earliestTopicStart, isFallback: false };
        }
      }
    }
  }
}

/**
 * Resolve how each series plots under the panel's alignment. See
 * {@link SeriesAlignment} for what the result means, including how consumers
 * treat a series it omits.
 */
export function resolveSeriesAlignment({
  series,
  sources,
  alignment,
  events,
  topics,
}: ResolveSeriesAlignmentParams): SeriesAlignment {
  const sourcesByFileId = groupSourcesByFileId(sources);

  // Only the map this alignment reads is built; building the other would walk
  // every workspace event or topic to no effect.
  const eventStartBySource = isEventAnchored(alignment)
    ? earliestEventStartBySource(
        alignment.anchor.eventName,
        events,
        sourcesByFileId,
        topics,
      )
    : undefined;
  const topicStartBySource =
    alignment.unit === "session" && alignment.anchor === "first-data"
      ? earliestTopicStartBySource(sourcesByFileId, topics)
      : undefined;

  const seriesById: Record<string, AlignedSeries> = {};
  for (const seriesItem of series) {
    const recording = seriesRecording(seriesItem, sourcesByFileId);
    if (recording === undefined) {
      continue;
    }
    const topicStart = parseNanosecondsString(seriesItem.data.topic.startTime);
    const { basis, isFallback } = resolveBasis(
      alignment,
      recording,
      topicStart,
      eventStartBySource,
      topicStartBySource,
    );
    seriesById[seriesItem.id] = {
      basis,
      sourceId: recording.sourceId,
      sourceStartTime: recording.startTime,
      sourceEndTime: recording.endTime,
      isFallback,
    };
  }

  return { seriesById };
}
